import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Clock4,
  FileSignature,
  FileText,
  GraduationCap,
  Hourglass,
  Loader2,
  Sparkles,
  ShieldAlert,
  Unlock,
  UserCheck,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type StageRequirementKind,
  type StageRequirementStatus,
  type StageRequirementStatus_DTO,
  type StageStatusResponse,
} from "@eduviaos/shared";
import { useTerms } from "@/hooks/useTerms";

/** Who fills the apply portal: a parent, or the learner themselves. */
function useApplicantNoun(): string {
  const { profile, terms } = useTerms();
  return profile.has.parents ? "parent" : terms.learner.one.toLowerCase();
}

function readErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
import {
  useAdvanceIfComplete,
  useAllowNextStage,
  useEnquiry,
  useForceAdvance,
  useStageStatus,
} from "@/hooks/useAdmissions";

/**
 * StageProgressPanel — Phase F0 FE
 * --------------------------------------------------------------
 * Surfaces the action-gated stage engine on the IA's enquiry-detail
 * page. Three responsibilities, in priority order:
 *
 *  1. **Visibility** — show the IA exactly which actions are still
 *     blocking the auto-advance (per-requirement chips with a status
 *     pill + a "why" detail line). This is the antidote to the
 *     pre-F0 "I clicked Advance and got a 422 — what now?" loop.
 *
 *  2. **Auto-advance CTA** — when verdict === `complete` AND a
 *     `next_stage_key` exists, surface a primary "Advance now"
 *     button. Idempotent against the BE; double-clicks return
 *     `advanced=false` and we suppress the toast.
 *
 *  3. **Admin override** — when the BE flags `can_force_advance`,
 *     reveal a destructive-style "Override gate…" button that opens
 *     a confirm dialog requiring a non-trivial reason (≥10 chars,
 *     enforced server-side too — the FE just spares the IA a
 *     round-trip when the field is obviously empty).
 *
 * State map (drives header chip + body):
 *  - `loading`     → skeleton row for ~150ms; rare in practice because
 *                    the panel mounts after the enquiry shell loads.
 *  - `error`       → calm muted alert; we don't block the rest of the
 *                    page so the IA can still edit the form.
 *  - `empty`       → "this stage has no requirements" — the legacy
 *                    explicit-advance flow at the bottom of the page
 *                    is the only way forward; the panel collapses to
 *                    a single helper line.
 *  - `pending`     → amber chip; nothing has started.
 *  - `partial`     → amber chip with progress copy "X of Y complete".
 *  - `complete`    → green chip + primary CTA.
 *  - `terminal`    → emerald/red chip when no `next_stage_key`; the
 *                    advance affordance is hidden, override is hidden,
 *                    we surface "Stage is final".
 *
 * Motion budget (Iron Rule #6 + emil-design-eng):
 *  - Status pill colour transition: 180ms easeOut, opacity-only.
 *  - Per-row appearance: stagger 30ms, opacity + 4px translateY only.
 *  - Override dialog: relies on the ui-kit Dialog's built-in fade.
 *  - All motion respects `prefers-reduced-motion`.
 *
 * A11y:
 *  - The card has `aria-live="polite"` so a screen reader announces
 *    when the verdict flips green and the CTA appears.
 *  - Each requirement row uses a `<dl>` so the label/status pairing
 *    is semantic, not just visual.
 *  - The override dialog re-states the destructive nature in body
 *    copy, not just the trigger button colour.
 */
export function StageProgressPanel({
  enquiryId,
  className,
}: {
  enquiryId: string;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  const applicant = useApplicantNoun();
  const stageStatusQuery = useStageStatus(enquiryId);
  const enquiryQuery = useEnquiry(enquiryId);
  const advanceMutation = useAdvanceIfComplete(enquiryId);
  const forceAdvanceMutation = useForceAdvance(enquiryId);
  const allowNextStageMutation = useAllowNextStage(enquiryId);

  const [overrideOpen, setOverrideOpen] = React.useState(false);
  const [overrideReason, setOverrideReason] = React.useState("");
  const [allowOpen, setAllowOpen] = React.useState(false);
  const [allowNote, setAllowNote] = React.useState("");

  const onAdvance = React.useCallback(async () => {
    try {
      const result = await advanceMutation.mutateAsync();
      if (result.advanced && result.to_stage_key) {
        toast.success(`Advanced to ${result.to_stage_key}.`);
      } else {
        // Idempotent no-op — either we raced another tab, or the
        // engine doesn't yet think this is complete. Tell the user
        // gently rather than silently swallowing the click.
        toast.info("Already up to date — no advance needed.");
      }
    } catch (err) {
      toast.error(readErrorMessage(err, "Couldn't advance the enquiry. Please try again."));
    }
  }, [advanceMutation]);

  const onConfirmAllowNextStage = React.useCallback(async () => {
    try {
      const trimmedNote = allowNote.trim();
      const result = await allowNextStageMutation.mutateAsync({
        next_stage_key: null,
        note: trimmedNote.length > 0 ? trimmedNote : null,
      });
      setAllowOpen(false);
      setAllowNote("");
      toast.success(
        `Apply portal can now render the ${result.unlocked_stage_key} form for the ${applicant}.`,
      );
    } catch (err) {
      toast.error(readErrorMessage(err, "Couldn't grant the next-stage unlock. Please try again."));
    }
  }, [allowNextStageMutation, allowNote, applicant]);

  const onConfirmForceAdvance = React.useCallback(async () => {
    const trimmed = overrideReason.trim();
    if (trimmed.length < 10) {
      // BE enforces 10..2000 chars; mirror the lower bound on the FE
      // so the IA gets immediate feedback instead of a 422 round-trip.
      // The submit button is disabled below 10 chars too, so this is
      // belt-and-braces — but if someone manages to bypass that
      // (e.g. via assistive tooling), surface the boundary in the
      // global toast layer rather than failing silently.
      toast.error("Reason must be at least 10 characters.");
      return;
    }
    try {
      const result = await forceAdvanceMutation.mutateAsync({ reason: trimmed });
      setOverrideOpen(false);
      setOverrideReason("");
      toast.success(`Overridden to ${result.to_stage_key}. Audit trail recorded.`);
    } catch (err) {
      toast.error(readErrorMessage(err, "Override failed. Please try again."));
    }
  }, [forceAdvanceMutation, overrideReason]);

  // ── Loading state ───────────────────────────────────────────────────────
  if (stageStatusQuery.isLoading) {
    return (
      <Card className={cn("border-border/60", className)} data-testid="stage-progress-panel">
        <CardHeader className="border-border/60 border-b pb-4">
          <CardTitle className="flex items-center gap-2 text-sm">
            <ClipboardList className="text-muted-foreground size-4" aria-hidden="true" />
            Stage progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-4">
          <SkeletonRow />
          <SkeletonRow />
        </CardContent>
      </Card>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (stageStatusQuery.isError || !stageStatusQuery.data) {
    return (
      <Card className={cn("border-border/60", className)} data-testid="stage-progress-panel">
        <CardHeader className="border-border/60 border-b pb-4">
          <CardTitle className="flex items-center gap-2 text-sm">
            <ClipboardList className="text-muted-foreground size-4" aria-hidden="true" />
            Stage progress
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p role="alert" className="text-muted-foreground flex items-start gap-2 text-xs">
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            Couldn&apos;t load stage requirements. The rest of the page still works — refresh to try
            again.
          </p>
        </CardContent>
      </Card>
    );
  }

  const status = stageStatusQuery.data;
  const verdictAppearance = pickVerdictAppearance(status);
  const isTerminal = status.next_stage_key === null;
  const canAdvance = status.verdict === "complete" && !isTerminal;
  const showOverride = status.can_force_advance && !isTerminal;
  const isAdvancing = advanceMutation.isPending;
  const isOverriding = forceAdvanceMutation.isPending;
  const isAllowing = allowNextStageMutation.isPending;
  // Phase H1 — apply-portal handoff. The CTA appears when the engine
  // says the current stage is complete (the gate has been honoured)
  // AND there's a next stage to unlock AND the parent hasn't already
  // been granted access. Showing it before `complete` would tempt
  // admins to bypass the gate without using the override path; the
  // override-button stays the canonical "I know what I'm doing"
  // affordance, while this CTA is the "let the parent finish" path.
  const enquiry = enquiryQuery.data;
  const unlockedStageKey = enquiry?.unlocked_stage_key ?? null;
  const showAllowNextStage = canAdvance && unlockedStageKey === null;
  const isUnlocked = unlockedStageKey !== null;

  return (
    <Card
      className={cn("border-border/60 overflow-hidden", verdictAppearance.cardAccent, className)}
      data-testid="stage-progress-panel"
      aria-live="polite"
    >
      <CardHeader className="border-border/60 border-b pb-4">
        <CardTitle className="flex items-center justify-between gap-3 text-sm">
          <span className="flex items-center gap-2">
            <ClipboardList className="text-muted-foreground size-4" aria-hidden="true" />
            Stage progress
          </span>
          <VerdictPill appearance={verdictAppearance} reduceMotion={reduceMotion ?? false} />
        </CardTitle>
        <p className="text-muted-foreground text-xs">
          {status.stage_label ?? status.stage_key}
          {!isTerminal && status.next_stage_key && (
            <>
              {" "}
              <span className="text-muted-foreground/60">→</span>{" "}
              <span className="text-foreground">{status.next_stage_key}</span>
            </>
          )}
          {isTerminal && (
            <>
              {" "}
              <span className="text-muted-foreground/60">·</span> stage is final
            </>
          )}
        </p>
      </CardHeader>

      <CardContent className="space-y-4 pt-4">
        {status.requirements.length === 0 ? (
          <p className="text-muted-foreground text-xs leading-relaxed">
            This stage has no required actions. Advance happens manually from the action bar at the
            bottom of the page (or via the funnel dropdown).
          </p>
        ) : (
          <dl className="space-y-2.5" data-testid="stage-requirement-list">
            {status.requirements.map((req, idx) => (
              <RequirementRow
                key={req.key}
                req={req}
                index={idx}
                reduceMotion={reduceMotion ?? false}
              />
            ))}
          </dl>
        )}

        {/* Phase H1 — surfaces the apply-portal handoff state. We show
            the chip ABOVE the toast and CTAs so the IA spots it
            before they accidentally advance the enquiry themselves. */}
        {isUnlocked && (
          <div
            data-testid="stage-unlocked-chip"
            className={cn(
              "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
              "border-violet-300/60 bg-violet-50 text-violet-900",
              "dark:border-violet-500/40 dark:bg-violet-950/40 dark:text-violet-200",
            )}
          >
            <Unlock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-medium">Next stage allowed</p>
              <p className="text-[11px] leading-relaxed">
                The apply portal can render{" "}
                <span className="font-semibold">{unlockedStageKey}</span> for the {applicant}. The
                grant is single-use — it clears the next time the enquiry moves forward.
              </p>
            </div>
          </div>
        )}

        {(canAdvance || showOverride || showAllowNextStage) && (
          <div className="flex flex-col gap-2 pt-1">
            {canAdvance && (
              <Button
                onClick={onAdvance}
                disabled={isAdvancing || isOverriding || isAllowing}
                className="justify-center"
                data-testid="stage-advance-btn"
              >
                {isAdvancing ? (
                  <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                ) : (
                  <ArrowRight aria-hidden="true" className="size-3.5" />
                )}
                {isAdvancing ? "Advancing…" : `Advance to ${status.next_stage_key}`}
              </Button>
            )}
            {showAllowNextStage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAllowNote("");
                  setAllowOpen(true);
                }}
                disabled={isAdvancing || isOverriding || isAllowing}
                data-testid="stage-allow-next-btn"
                className="border-violet-300/60 text-violet-700 hover:bg-violet-50 dark:border-violet-500/40 dark:text-violet-300 dark:hover:bg-violet-950/40"
              >
                <Unlock aria-hidden="true" className="size-3.5" />
                Allow {applicant} to fill on apply portal…
              </Button>
            )}
            {showOverride && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setOverrideReason("");
                  setOverrideOpen(true);
                }}
                disabled={isAdvancing || isOverriding || isAllowing}
                data-testid="stage-override-btn"
                className="border-amber-300/60 text-amber-700 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-300 dark:hover:bg-amber-950/40"
              >
                <ShieldAlert aria-hidden="true" className="size-3.5" />
                Override gate…
              </Button>
            )}
          </div>
        )}
      </CardContent>

      {/* Override-gate confirmation dialog. We isolate the destructive
          intent here (rather than inline-confirm in the card) so the
          IA has to actively type a justification — the BE refuses
          reasons under 10 chars but the FE saves a round-trip. */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert
                className="size-4 text-amber-600 dark:text-amber-400"
                aria-hidden="true"
              />
              Override the stage gate?
            </DialogTitle>
            <DialogDescription>
              The action-gated engine says this stage isn&apos;t complete. Overriding skips the gate
              and advances the enquiry anyway. Your reason is logged to the audit trail and surfaces
              on the override-rate dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="override-reason" className="text-xs font-medium">
              Reason for override <span className="text-muted-foreground">(min 10 chars)</span>
            </Label>
            <Textarea
              id="override-reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder={`e.g. ${applicant} submitted documents in person; assessment was conducted offline on 2026-04-10.`}
              className="min-h-[88px]"
              maxLength={2000}
              data-testid="stage-override-reason"
              autoFocus
              disabled={isOverriding}
            />
            <p className="text-muted-foreground text-[11px]">
              {overrideReason.trim().length}/2000 characters
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm" disabled={isOverriding}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={onConfirmForceAdvance}
              disabled={isOverriding || overrideReason.trim().length < 10}
              size="sm"
              className="bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600"
              data-testid="stage-override-confirm"
            >
              {isOverriding ? (
                <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
              ) : (
                <ShieldAlert aria-hidden="true" className="size-3.5" />
              )}
              {isOverriding ? "Overriding…" : "Override and advance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase H1 — apply-portal handoff confirmation. The note field
          is OPTIONAL (the BE accepts null) but we surface it so the
          IA can leave a one-liner explaining why they're handing off
          to the parent. The audit trail keeps a permanent record. */}
      <Dialog open={allowOpen} onOpenChange={setAllowOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="size-4 text-violet-600 dark:text-violet-400" aria-hidden="true" />
              Allow the {applicant} to fill the next stage?
            </DialogTitle>
            <DialogDescription>
              Once granted, the apply portal will render the{" "}
              <span className="font-medium">{status.next_stage_key}</span> form for the {applicant}
              instead of the current stage&apos;s form. The grant is single-use — it clears the next
              time the enquiry moves forward.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="allow-note" className="text-xs font-medium">
              Note <span className="text-muted-foreground">(optional, audit-logged)</span>
            </Label>
            <Textarea
              id="allow-note"
              value={allowNote}
              onChange={(e) => setAllowNote(e.target.value)}
              placeholder={`e.g. ${applicant} confirmed they want to fill the application form themselves over the weekend.`}
              className="min-h-[72px]"
              maxLength={500}
              data-testid="stage-allow-next-note"
              autoFocus
              disabled={isAllowing}
            />
            <p className="text-muted-foreground text-[11px]">
              {allowNote.trim().length}/500 characters
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm" disabled={isAllowing}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={onConfirmAllowNextStage}
              disabled={isAllowing}
              size="sm"
              className="bg-violet-600 text-white hover:bg-violet-700 dark:bg-violet-500 dark:hover:bg-violet-600"
              data-testid="stage-allow-next-confirm"
            >
              {isAllowing ? (
                <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
              ) : (
                <Unlock aria-hidden="true" className="size-3.5" />
              )}
              {isAllowing ? "Granting…" : "Allow next stage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function RequirementRow({
  req,
  index,
  reduceMotion,
}: {
  req: StageRequirementStatus_DTO;
  index: number;
  reduceMotion: boolean;
}) {
  const Icon = pickKindIcon(req.kind);
  const appearance = pickStatusAppearance(req.status);
  const applicant = useApplicantNoun();
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduceMotion ? 0 : 0.18,
        delay: reduceMotion ? 0 : Math.min(index * 0.03, 0.18),
        ease: "easeOut",
      }}
      className="border-border/60 bg-card flex items-start gap-3 rounded-md border px-3 py-2.5"
      data-testid={`stage-requirement-row-${req.key}`}
      data-status={req.status}
    >
      <span
        className={cn(
          "mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md",
          appearance.iconBg,
        )}
        aria-hidden="true"
      >
        <Icon className={cn("size-3.5", appearance.iconColor)} />
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-foreground text-xs font-medium leading-tight">
          {req.label}{" "}
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="text-muted-foreground inline-flex cursor-default text-[10px] uppercase tracking-wider"
                  aria-label={`Requirement type: ${prettifyKind(req.kind)}`}
                >
                  · {prettifyKind(req.kind)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="max-w-[220px] text-xs">{kindDescription(req.kind, applicant)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </dt>
        {req.detail && (
          <dd className="text-muted-foreground mt-0.5 text-[11px] leading-relaxed">{req.detail}</dd>
        )}
      </div>
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
          appearance.pillClass,
        )}
        data-testid={`stage-requirement-pill-${req.key}`}
      >
        <appearance.pillIcon className="size-3" aria-hidden="true" />
        {appearance.pillLabel}
      </span>
    </motion.div>
  );
}

interface VerdictAppearance {
  pillLabel: string;
  pillClass: string;
  pillIconClass: string;
  Icon: LucideIcon;
  cardAccent: string;
  helperCopy: string | null;
}

function VerdictPill({
  appearance,
  reduceMotion,
}: {
  appearance: VerdictAppearance;
  reduceMotion: boolean;
}) {
  return (
    <AnimatePresence initial={false} mode="wait">
      <motion.span
        key={appearance.pillLabel}
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.18, ease: "easeOut" }}
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
          appearance.pillClass,
        )}
        data-testid="stage-progress-verdict"
      >
        <appearance.Icon className={cn("size-3", appearance.pillIconClass)} aria-hidden="true" />
        {appearance.pillLabel}
      </motion.span>
    </AnimatePresence>
  );
}

function SkeletonRow() {
  return (
    <div className="border-border/60 bg-card flex items-center gap-3 rounded-md border px-3 py-2.5">
      <span className="bg-muted size-7 shrink-0 animate-pulse rounded-md" aria-hidden="true" />
      <div className="flex-1 space-y-1.5">
        <span className="bg-muted block h-3 w-2/3 animate-pulse rounded" aria-hidden="true" />
        <span className="bg-muted block h-2.5 w-1/2 animate-pulse rounded" aria-hidden="true" />
      </div>
      <span className="bg-muted h-4 w-12 animate-pulse rounded-full" aria-hidden="true" />
    </div>
  );
}

// ── Lookup tables ─────────────────────────────────────────────────────────

function pickVerdictAppearance(status: StageStatusResponse): VerdictAppearance {
  const total = status.requirements.length;
  const completed = status.requirements.filter((r) => r.status === "complete").length;
  const blocked = status.requirements.some((r) => r.status === "blocked");
  if (status.next_stage_key === null) {
    // Stage is terminal — verdict is informational only.
    return {
      pillLabel: status.verdict === "complete" ? "Final · complete" : "Final stage",
      pillClass:
        "border border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-300",
      pillIconClass: "",
      Icon: GraduationCap,
      cardAccent: "",
      helperCopy: null,
    };
  }
  if (blocked) {
    return {
      pillLabel: "Blocked",
      pillClass:
        "border border-rose-300/60 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-950/40 dark:text-rose-200",
      pillIconClass: "",
      Icon: XCircle,
      cardAccent:
        "border-rose-200/60 bg-gradient-to-br from-rose-50/40 to-transparent dark:border-rose-500/30 dark:from-rose-950/20",
      helperCopy: "A required action is misconfigured. A Super Admin needs to fix it.",
    };
  }
  if (status.verdict === "complete") {
    return {
      pillLabel: "Ready to advance",
      pillClass:
        "border border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-300",
      pillIconClass: "",
      Icon: CheckCircle2,
      cardAccent:
        "border-emerald-200/60 bg-gradient-to-br from-emerald-50/40 to-transparent dark:border-emerald-500/30 dark:from-emerald-950/20",
      helperCopy: null,
    };
  }
  if (status.verdict === "partially_complete") {
    return {
      pillLabel: total > 0 ? `${completed} of ${total}` : "In progress",
      pillClass:
        "border border-amber-300/60 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200",
      pillIconClass: "",
      Icon: Hourglass,
      cardAccent: "",
      helperCopy: null,
    };
  }
  if (status.verdict === "not_applicable") {
    return {
      pillLabel: "No requirements",
      pillClass: "border-border/70 text-muted-foreground bg-muted/40 border",
      pillIconClass: "",
      Icon: Sparkles,
      cardAccent: "",
      helperCopy: null,
    };
  }
  // pending
  return {
    pillLabel: "Pending",
    pillClass:
      "border border-amber-300/60 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200",
    pillIconClass: "",
    Icon: Clock4,
    cardAccent: "",
    helperCopy: null,
  };
}

interface StatusAppearance {
  iconBg: string;
  iconColor: string;
  pillLabel: string;
  pillClass: string;
  pillIcon: LucideIcon;
}

function pickStatusAppearance(status: StageRequirementStatus): StatusAppearance {
  switch (status) {
    case "complete":
      return {
        iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
        iconColor: "text-emerald-700 dark:text-emerald-300",
        pillLabel: "Done",
        pillClass:
          "border border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-200",
        pillIcon: CheckCircle2,
      };
    case "in_progress":
      return {
        iconBg: "bg-violet-100 dark:bg-violet-900/40",
        iconColor: "text-violet-700 dark:text-violet-300",
        pillLabel: "Started",
        pillClass:
          "border border-violet-300/60 bg-violet-50 text-violet-700 dark:border-violet-500/40 dark:bg-violet-950/40 dark:text-violet-200",
        pillIcon: Loader2,
      };
    case "blocked":
      return {
        iconBg: "bg-rose-100 dark:bg-rose-900/40",
        iconColor: "text-rose-700 dark:text-rose-300",
        pillLabel: "Blocked",
        pillClass:
          "border border-rose-300/60 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-950/40 dark:text-rose-200",
        pillIcon: XCircle,
      };
    case "not_applicable":
      return {
        iconBg: "bg-muted",
        iconColor: "text-muted-foreground",
        pillLabel: "N/A",
        pillClass: "border-border/70 text-muted-foreground bg-muted/40 border",
        pillIcon: Sparkles,
      };
    case "pending":
    default:
      return {
        iconBg: "bg-amber-100 dark:bg-amber-900/40",
        iconColor: "text-amber-700 dark:text-amber-300",
        pillLabel: "Pending",
        pillClass:
          "border border-amber-300/60 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200",
        pillIcon: Clock4,
      };
  }
}

function pickKindIcon(kind: StageRequirementKind): LucideIcon {
  switch (kind) {
    case "form_submission":
      return FileSignature;
    case "document_upload":
      return FileText;
    case "assessment_attempt":
      return ClipboardList;
    case "interview_completed":
      return UserCheck;
    case "manual_check":
    default:
      return CheckCircle2;
  }
}

function prettifyKind(kind: StageRequirementKind): string {
  switch (kind) {
    case "form_submission":
      return "Form";
    case "document_upload":
      return "Document";
    case "assessment_attempt":
      return "Assessment";
    case "interview_completed":
      return "Interview";
    case "manual_check":
    default:
      return "Manual";
  }
}

function kindDescription(kind: StageRequirementKind, applicant: string): string {
  switch (kind) {
    case "form_submission":
      return `Marks complete when the linked form is submitted (filled by your team, or by the ${applicant} on the apply portal).`;
    case "document_upload":
      return "Marks complete when the configured documents are uploaded and accepted.";
    case "assessment_attempt":
      return "Marks complete when the assigned assessment is attempted (and, if configured, passed).";
    case "interview_completed":
      return "Marks complete when an interview slot is held and the assessor records the outcome.";
    case "manual_check":
    default:
      return "Marks complete when an admin checks it off — useful for offline steps the system can't see.";
  }
}
